import { useCallback, useContext, useEffect, useRef, useState, type RefObject } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { forter3DSService } from '@/services/forter3DSService';
import { useThreeDSComplete } from '@/api/threeDSComplete';
import { logError, logInfo } from '@/utils/logger';
import { ErrorLogs, InfoLogs } from '@/constants/logs';

interface UseForter3DSChallengeReturn {
  challengeContainerRef: RefObject<HTMLDivElement>;
  /**
   * `true` once the Forter SDK has requested the container to inject its
   * iframe into — i.e. an interactive challenge is actually about to render.
   * Stays `false` on the frictionless path (SDK resolves without ever calling
   * `challengeContainer`), letting the consumer keep the modal chrome hidden
   * so the shopper never sees a brief flash of an empty modal before the
   * normal success/failure dispatch lands.
   */
  hasChallenge: boolean;
  /**
   * `true` once the shopper has interacted with the challenge (Forter removed
   * its iframe and resolved the SDK promise) and we're waiting on the
   * `/3ds/complete` round-trip. Lets the consumer swap the now-empty
   * container for a "verifying…" panel so the modal doesn't look stuck.
   * Stays `true` until the form unmounts on the dispatched outcome.
   */
  isCompleting: boolean;
  cancel: () => void;
}

/**
 * Drives the Forter-orchestrated 3DS lifecycle on behalf of
 * <ForterCard3DSForm/>. Reads the active challenge state from
 * `threeDSSecurityState`, runs the Forter SDK against
 * `challengeContainerRef`, then POSTs `/3ds/complete` with the paired
 * `paymentAttemptId` (mirrors how <AdyenCard3DSForm/> calls `/status`
 * internally). The `/3ds/complete` response has the same shape as
 * `/finalize-payment`, so it's piped back to `handleThreeDSCompleteResponse`
 * which re-enters `usePaymentResults` for the standard PSP dispatch — the
 * form stays presentation-only and no PSP routing leaks here.
 *
 * Outcome routing (mirrors the rest of the 3DS callback contract):
 *   - SDK rejects                  → `handlePaymentError()`
 *   - `/3ds/complete` rejects      → `handlePaymentError()`
 *   - `/3ds/complete` resolves     → `handleThreeDSCompleteResponse(response)`
 *   - `paymentAttemptId` missing   → `handlePaymentError()` (BE contract violation)
 *   - `cancel()` invoked           → `handlePaymentCancel()` (manual exit)
 *
 * `cancel()` is the user-facing escape hatch (the modal X, or a stuck
 * iframe). Closing the challenge is a shopper walk-away, not a decline, so
 * it resets checkout without an error message or PAYMENT_INTENT_FAILED. The
 * Forter SDK exposes no abort API, so the in-flight promise still settles
 * after cancellation; an `isCancelled` ref guards the late `.then` /
 * `.catch` so success completion can't fire after the shopper has bailed out.
 */
export const useForter3DSChallenge = (): UseForter3DSChallengeReturn => {
  const {
    threeDSSecurityState: {
      managedOrderToken,
      paymentAttemptId,
      handlePaymentError,
      handlePaymentCancel,
      handleThreeDSCompleteResponse,
    },
  } = useContext(CheckoutContext);
  const challengeContainerRef = useRef<HTMLDivElement>(null);
  const isProcessing = useRef(false);
  const isCancelled = useRef(false);
  const [hasChallenge, setHasChallenge] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const threeDSComplete = useThreeDSComplete();

  useEffect(() => {
    if (!managedOrderToken || isProcessing.current) {
      return;
    }

    isProcessing.current = true;
    isCancelled.current = false;
    logInfo(InfoLogs.forter3dsChallengeStarted);

    forter3DSService
      .manageOrder(managedOrderToken, {
        // SDK invokes this only when an interactive challenge is required
        // (frictionless flow skips it entirely). Flip `hasChallenge` here so
        // the consumer can promote the modal from hidden to visible.
        challengeContainer: () => {
          setHasChallenge(true);
          return challengeContainerRef.current;
        },
      })
      .then(async () => {
        if (isCancelled.current) return;
        logInfo(InfoLogs.forter3dsChallengeSuccess);
        // Forter has removed the iframe at this point; flip into the
        // completing state so the modal shows a "verifying…" panel instead
        // of an empty container during the `/3ds/complete` round-trip.
        setIsCompleting(true);
        await completeThreeDS();
      })
      .catch((error) => {
        if (isCancelled.current) return;
        logError(ErrorLogs.forter3dsChallengeError, error);
        handlePaymentError?.();
      })
      .finally(() => {
        isProcessing.current = false;
      });
    // Run once per challenge token; handlers are stable for a given challenge.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managedOrderToken]);

  const completeThreeDS = async () => {
    // `paymentAttemptId` is part of the BE contract: it MUST come back
    // alongside `managedOrderToken`. If it's missing we can't call
    // `/3ds/complete`, and we can't claim success either — the 3DS result is
    // unknown. Fail loudly so the shopper retries instead of silently
    // confirming an unverified payment.
    if (!paymentAttemptId) {
      logError(ErrorLogs.forter3dsChallengeError, undefined, { reason: 'missing_payment_attempt_id' });
      handlePaymentError?.();
      return;
    }
    try {
      const completeResults = await threeDSComplete.mutateAsync({ paymentAttemptId });
      await handleThreeDSCompleteResponse?.(completeResults);
    } catch (error) {
      logError(ErrorLogs.forter3dsChallengeError, error, { reason: '3ds_complete_failed' });
      handlePaymentError?.();
    }
  };

  const cancel = useCallback(() => {
    if (isCancelled.current) return;
    isCancelled.current = true;
    logInfo(InfoLogs.checkoutPaymentCancelled, { reason: 'user_cancelled' });
    handlePaymentCancel?.();
  }, [handlePaymentCancel]);

  return { challengeContainerRef, hasChallenge, isCompleting, cancel };
};
